<template>
	<div id="challenger-results">
		<div class="complete-heading">
			<p>Comparing {{ selectedDateRange }} using {{ challengerProfile.profileLabel }}</p>
			<v-btn text @click="newComparison()">Try a New Comparison</v-btn>
		</div>

		<div class="complete-stats" v-if="challengerDecisionDeltaPercentages.pay !== undefined && oldDecisionSeries.length > 0">
			<div>
				<apexChart type="pie" width="420" id="old-decision-chart" :options="options" :series="oldDecisionSeries"></apexChart>
				<h3 class="complete-stats-header">Old Profile</h3>
				<label for="old-decision-chart" class="complete-stats-label">{{ challengerProfile.profileLabel }}</label>
			</div>
			<div class="challenge-results">
				<h3>Challenge Results</h3>
				<div  class="complete-results-grid">
					<div>Paid: </div>
					<div class="complete-results-grid-percentage">
						<template v-if="challengerDecisionDeltaPercentages.pay > 0">
							<template>{{ (challengerDecisionDeltaPercentages.pay % 1 ? challengerDecisionDeltaPercentages.pay.toFixed(1) : challengerDecisionDeltaPercentages.pay)}}% </template>
							<v-icon class="up-arrow">mdi-arrow-up-bold</v-icon>
						</template>
						<template v-else-if="challengerDecisionDeltaPercentages.pay < 0">
							<template>{{ -1*(challengerDecisionDeltaPercentages.pay % 1 ? challengerDecisionDeltaPercentages.pay.toFixed(1) : challengerDecisionDeltaPercentages.pay) }}% </template>
							<v-icon class="down-arrow">mdi-arrow-down-bold</v-icon>
						</template>
						<template v-else>Equal</template>
					</div>
					<div>Denied: </div>
					<div class="complete-results-grid-percentage">
						<template v-if="challengerDecisionDeltaPercentages.deny > 0">
							<template>{{ (challengerDecisionDeltaPercentages.deny % 1 ? challengerDecisionDeltaPercentages.deny.toFixed(1) : challengerDecisionDeltaPercentages.deny)}}% </template>
							<v-icon class="up-arrow">mdi-arrow-up-bold</v-icon>
						</template>
						<template v-else-if="challengerDecisionDeltaPercentages.deny < 0">
							<template>{{ -1*(challengerDecisionDeltaPercentages.deny % 1 ? challengerDecisionDeltaPercentages.deny.toFixed(1) : challengerDecisionDeltaPercentages.deny) }}% </template>
							<v-icon class="down-arrow">mdi-arrow-down-bold</v-icon>
						</template>
						<template v-else>Equal</template>
					</div>
					<div>Referred: </div>
					<div class="complete-results-grid-percentage">
						<template v-if="challengerDecisionDeltaPercentages.refer > 0">
							<template>{{ (challengerDecisionDeltaPercentages.refer % 1 ? challengerDecisionDeltaPercentages.refer.toFixed(1) : challengerDecisionDeltaPercentages.refer) }}% </template>
							<v-icon class="up-arrow">mdi-arrow-up-bold</v-icon>
						</template>
						<template v-else-if="challengerDecisionDeltaPercentages.refer < 0">
							<template>{{ -1*(challengerDecisionDeltaPercentages.refer % 1 ? challengerDecisionDeltaPercentages.refer.toFixed(1) : challengerDecisionDeltaPercentages.refer) }}% </template>
							<v-icon class="down-arrow">mdi-arrow-down-bold</v-icon>
						</template>
						<template v-else>Equal</template>
					</div>
				</div>
			</div>

			<div>
				<apexChart type="pie" width="420" id="new-decision-chart" :options="options" :series="newDecisionSeries"></apexChart>
				<h3 class="complete-stats-header">New Profile</h3>
				<label for="new-decision-chart" class="complete-stats-label">{{ profileLabel }}</label>
			</div>
		</div>
		<div v-else>
			<v-skeleton-loader type="image"></v-skeleton-loader>
		</div>

		<div v-if="challengerResultList.length > 0 && challengerDifferentOutcomeList.length > 0" class="complete-results-different-outcome">
			<label for="complete-results-table">{{challengerDifferentOutcomeList.length}} out of {{challengerResultList.length}} disputes have a different outcome</label>
			<v-data-table
			:headers="challengerResultHeaders"
			:items="challengerDifferentOutcomeList"
			:items-per-page="10"
			:hide-default-footer="challengerDifferentOutcomeList.length <= 10"
			mobile-breakpoint="500"
			id="complete-results-table"
			>
				<template v-slot:item.decision="{ item }">
					<div class="complete-results-decision-compare">
						<v-chip :class="item.decisionOld">{{item.decisionOld}}</v-chip>
						<v-icon>mdi-ray-start-arrow</v-icon>
						<v-chip :class="item.decisionNew">{{item.decisionNew}}</v-chip>
					</div>
				</template>

				<template v-slot:item.details="{ item }">
					<disputeDetailsDialogBox :item="item"/>
				</template>

			</v-data-table>
		</div>
		<div v-else-if="challengerDifferentOutcomeList.length === 0 && challengerResultList.length > 0" class="complete-results-no-difference">
			<h3>No Differences Found</h3>
			<img class="empty-state-graphic" src="@/assets/imgs/clone-sheep.svg">
		</div>
		<div v-else>
			<v-skeleton-loader type="table"></v-skeleton-loader>
		</div>
	</div>
</template>

<script>
	/**
	 * @fileoverview challenger-results.vue is the last of three components used for the Champion Challenger. It allows the user
	 * to view the results of the challenger. It relies on vuex to map the state variables from the store files.
	 * 
	 * @components
	 * 		disputeDetailsDialogBox - modal dialog box used to display dispute details
	 * 
	 * @author Sophia Frankel <sophia.frankel@quavo.com>
	 */
	import { mapState } from 'vuex';
	import disputeDetailsDialogBox from '@/components/configuration-explorer/dispute-details-dialog-box.vue';

	export default {
		props: {
			/**
			 * Original profile used for the champion challenger comparison
			 */
			challengerProfile: {
				required: true,
				type: Object
			}
		},
		data() {
			return {
				deployReady: false,	// true when profile is ready to be deloyed
				/**
				 * Headers for the disputes with different outcomes list
				 */
				challengerResultHeaders: [
					{
						text: "Dispute Number",
						align: "start",
						value: "externalDisputeId",
					},
					{
						text: "Decision",
						align: "center",
						value: "decision",
					},
					{
						align: "end",
						value: "details"
					}
				],
				/**
				 * Options for the pie charts
				 */
				options: {
					chart: {
						dropShadow: {
							enabled: true,
							bottom: 5,
							left: 0,
							blur: 3,
							opacity: 0.5
						},
					},
					legend: {
						show: false
					},
					colors: ['#4EA674', '#C98724', '#AE2727'],
					labels: ["Pay", "Refer", "Deny"],
					dataLabels: {
						style: {
							fontSize: '1em',
							fontFamily: 'Avenir, Helvetica, Arial, sans-serif',
						},
						formatter: function(val) {
							//console.log(opt);
							return (val.toString().includes(".") ? val.toFixed(1) : val) + "%";
							//return val + "%" + "\n" + opt.w.globals.labels[opt.seriesIndex];
						},
					},
					plotOptions: {
						pie: {
							customScale: 1
						}
					}
				},
				oldDecisionSeries: [],	// Series for the old dispute decision counts
				newDecisionSeries: [],	// Series for the new dispute decision counts
			}
		},
		components: {
			disputeDetailsDialogBox
		},
		computed: {
			...mapState({
				challengerRange: (state) => state.config.challengerRange,
				challengerDateList: (state) => state.config.challengerDateList,
				challengerDecisionDeltaPercentages: (state) => state.config.challengerDecisionDeltaPercentages,
				challengerOldDecisionCounts: (state) => state.config.challengerOldDecisionCounts,
				challengerNewDecisionCounts: (state) => state.config.challengerNewDecisionCounts,
				challengerResultList: (state) => state.config.challengerResultList,
				challengerDifferentOutcomeList: (state) => state.config.challengerDifferentOutcomeList,
				profileLabel: (state) => state.config.configProfile.profileLabel,
			}),
			/**
			 * Get the selected date range
			 * @returns the selected date range label
			 */
			selectedDateRange() {
				if (this.challengerRange !== "") {
					return this.challengerDateList.find(r => r.value === this.challengerRange).label;
				}
				return "";
			},
		},
		watch: {
			/**
			 * Set deployReady flag in the config store
			 */
			deployReady() {
				this.$store.dispatch("setAllowProfileDeploy", this.deployReady);
			},
			/**
			 * Create the oldDecisionSeries out of the challengerOldDecisionCounts
			 */
			challengerOldDecisionCounts() {
				let oldCounts = [];
				oldCounts.push(this.challengerOldDecisionCounts.pay);
				oldCounts.push(this.challengerOldDecisionCounts.refer);
				oldCounts.push(this.challengerOldDecisionCounts.deny);

				this.oldDecisionSeries = oldCounts;
			},
			/**
			 * Create the newDecisionSeries out of the challengerNewDecisionCounts
			 */
			challengerNewDecisionCounts() {
				let newCounts = [];
				newCounts.push(this.challengerNewDecisionCounts.pay);
				newCounts.push(this.challengerNewDecisionCounts.refer);
				newCounts.push(this.challengerNewDecisionCounts.deny);

				this.newDecisionSeries = newCounts;
			},
		},
		methods: {
			/**
			 * Clear challenger data and set deployReady to true
			 */
			async newComparison() {
				await this.$store.dispatch("clearChallengerData");
				this.deployReady = true;
			},
		}
	}
</script>

<style>

</style>